
import { AWSModule } from '../types';
import { generateStandardizedContent } from '@/utils/contentGeneration';

export const ENHANCED_AWS_FUNDAMENTALS_MODULES: AWSModule[] = [
  {
    id: 'aws-cloud-introduction',
    title: 'Introduction to Cloud Computing and AWS',
    description: 'Understanding cloud computing fundamentals and the AWS ecosystem',
    content: generateStandardizedContent({
      moduleTitle: 'Introduction to Cloud Computing and AWS',
      courseType: 'aws',
      difficulty: 'beginner',
      estimatedTime: '45 minutes',
      learningObjectives: [
        'Understand the fundamentals of cloud computing',
        'Identify the benefits of AWS cloud services',
        'Navigate the AWS Management Console',
        'Recognize core AWS global infrastructure components'
      ],
      prerequisites: ['Basic understanding of IT concepts', 'Familiarity with web browsers'],
      keyConceptsData: {
        concept1: {
          title: 'What is Cloud Computing?',
          explanation: 'Cloud computing is the on-demand delivery of IT resources over the Internet with pay-as-you-go pricing. Instead of buying, owning, and maintaining physical data centers and servers, you can access technology services on an as-needed basis from cloud providers like AWS.',
          importance: 'Cloud computing eliminates the need for large upfront investments in hardware and allows businesses to scale resources up or down based on demand.',
          example: 'Instead of purchasing and maintaining physical servers in your office, you can rent virtual servers from AWS that can be started, stopped, and resized as needed.'
        },
        concept2: {
          title: 'AWS Global Infrastructure',
          explanation: 'AWS operates in multiple geographic regions around the world, each containing multiple isolated data centers called Availability Zones.',
          bestPractices: [
            'Choose regions close to your users for lower latency',
            'Use multiple Availability Zones for high availability',
            'Consider data sovereignty and compliance requirements when selecting regions'
          ]
        },
        concept3: {
          title: 'AWS Management Console',
          explanation: 'The AWS Management Console is a web-based interface for accessing and managing AWS services. It provides a user-friendly way to create, configure, and monitor AWS resources.'
        }
      },
      practicalExamples: {
        example1: {
          title: 'Launching Your First EC2 Instance',
          description: 'Step-by-step guide to launching a virtual server in AWS',
          code: `# Connect to your EC2 instance via SSH
ssh -i "your-key.pem" ec2-user@your-instance-public-ip

# Update the system
sudo yum update -y

# Install a web server
sudo yum install -y httpd
sudo systemctl start httpd
sudo systemctl enable httpd`,
          explanation: 'This example shows how to connect to and configure a basic web server on an EC2 instance.'
        },
        example2: {
          title: 'Creating an S3 Bucket',
          description: 'How to create and configure object storage in AWS',
          code: `# Using AWS CLI to create an S3 bucket
aws s3 mb s3://my-unique-bucket-name-12345

# Upload a file to the bucket
aws s3 cp myfile.txt s3://my-unique-bucket-name-12345/

# List contents of the bucket
aws s3 ls s3://my-unique-bucket-name-12345/`,
          keyPoints: [
            'Bucket names must be globally unique across all AWS accounts',
            'Choose appropriate storage classes based on access patterns'
          ]
        }
      },
      industryContext: {
        usage: 'Major enterprises like Netflix, Airbnb, and Spotify rely on AWS to power their global applications, handling millions of users and petabytes of data.',
        tools: [
          { name: 'AWS CLI', description: 'Command-line interface for managing AWS services' },
          { name: 'AWS CloudFormation', description: 'Infrastructure as Code service for automated resource management' },
          { name: 'AWS CloudWatch', description: 'Monitoring and observability service for AWS resources' }
        ],
        careerRelevance: 'AWS skills are in high demand, with cloud engineers earning 15-25% more than traditional IT roles. AWS certifications are recognized globally and can significantly boost career prospects.'
      },
      takeaways: [
        'Cloud computing provides scalable, on-demand IT resources',
        'AWS offers a comprehensive suite of cloud services',
        'The AWS Management Console simplifies resource management'
      ],
      pitfalls: [
        'Not considering data transfer costs between regions',
        'Forgetting to terminate unused resources leading to unexpected bills'
      ],
      nextSteps: [
        'Explore core AWS services in the next module',
        'Practice with the AWS Free Tier to gain hands-on experience'
      ]
    }),
    duration: '45 minutes',
    difficulty: 'beginner',
    order: 1,
    learningObjectives: [
      'Understand cloud computing fundamentals',
      'Navigate AWS Management Console',
      'Identify AWS global infrastructure components',
      'Recognize cloud computing benefits'
    ],
    prerequisites: ['Basic IT knowledge'],
    estimatedTime: '45 minutes',
    practice: {
      task: 'Set up your AWS account and explore the Management Console. Launch a free tier EC2 instance and create an S3 bucket.',
      starter: '# Follow these steps to get started with AWS:\n\n1. Sign up for AWS Free Tier\n2. Access the AWS Management Console\n3. Launch an EC2 instance\n4. Create an S3 bucket',
      solution: `# Complete AWS Setup Checklist:

✅ AWS Account Setup:
1. Navigate to aws.amazon.com
2. Click "Create an AWS Account"
3. Follow the registration process
4. Verify your email and phone number

✅ AWS Management Console:
1. Sign in to the AWS Management Console
2. Explore the main dashboard
3. Use the search bar to find services
4. Bookmark frequently used services

✅ Launch EC2 Instance:
1. Navigate to EC2 service
2. Click "Launch Instance"
3. Choose Amazon Linux 2 AMI
4. Select t2.micro (Free Tier eligible)
5. Configure security group to allow SSH
6. Launch with a new key pair

✅ Create S3 Bucket:
1. Navigate to S3 service
2. Click "Create bucket"
3. Choose a unique bucket name
4. Select your preferred region
5. Keep default settings for now
6. Click "Create bucket"`,
      hints: [
        'Use the AWS Free Tier to avoid charges during learning',
        'Always terminate resources when not in use to avoid unexpected costs',
        'Keep your AWS credentials secure and never share them'
      ]
    },
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the primary benefit of cloud computing?',
          options: [
            'On-demand access to IT resources with pay-as-you-go pricing',
            'Requires large upfront hardware investments',
            'Only suitable for large enterprises',
            'Limited to web applications only'
          ],
          correctAnswer: 0
        },
        {
          id: 'q2',
          question: 'What is an AWS Availability Zone?',
          options: [
            'A geographic region',
            'An isolated data center within a region',
            'A type of EC2 instance',
            'A storage service'
          ],
          correctAnswer: 1
        },
        {
          id: 'q3',
          question: 'Which AWS service provides virtual servers?',
          options: ['S3', 'EC2', 'RDS', 'CloudWatch'],
          correctAnswer: 1
        }
      ]
    }
  },
  {
    id: 'aws-core-services',
    title: 'Core AWS Services (EC2, S3, RDS, VPC)',
    description: 'Deep dive into essential AWS services for compute, storage, database, and networking',
    content: generateStandardizedContent({
      moduleTitle: 'Core AWS Services (EC2, S3, RDS, VPC)',
      courseType: 'aws',
      difficulty: 'beginner',
      estimatedTime: '60 minutes',
      learningObjectives: [
        'Configure and manage EC2 instances for various workloads',
        'Implement secure and scalable storage solutions with S3',
        'Set up managed databases using RDS',
        'Design secure network architectures with VPC'
      ],
      prerequisites: ['AWS account setup', 'Basic understanding of cloud computing'],
      keyConceptsData: {
        concept1: {
          title: 'Amazon EC2 (Elastic Compute Cloud)',
          explanation: 'EC2 provides resizable compute capacity in the cloud. You can launch virtual servers called instances, choose from various instance types optimized for different use cases.',
          importance: 'EC2 is the backbone of most AWS applications, providing the compute power needed to run applications, websites, and services.',
          example: 'A startup can start with a single t2.micro instance for their web application and scale up to multiple larger instances as their user base grows.'
        },
        concept2: {
          title: 'Amazon S3 (Simple Storage Service)',
          explanation: 'S3 is object storage built to store and retrieve any amount of data from anywhere. It offers industry-leading scalability, data availability, security, and performance.',
          bestPractices: [
            'Use appropriate storage classes to optimize costs',
            'Implement versioning for critical data',
            'Configure lifecycle policies for automated data management'
          ]
        },
        concept3: {
          title: 'Amazon RDS and VPC Networking',
          explanation: 'RDS makes it easy to set up, operate, and scale relational databases. VPC lets you provision a logically isolated section of AWS cloud.'
        }
      },
      practicalExamples: {
        example1: {
          title: 'EC2 Instance Configuration',
          description: 'Setting up a web server with proper security groups',
          code: `# Launch EC2 instance with user data script
#!/bin/bash
yum update -y
yum install -y httpd
systemctl start httpd
systemctl enable httpd
echo "<h1>Hello from AWS EC2!</h1>" > /var/www/html/index.html

# Security Group Configuration
# Inbound Rules:
# HTTP (80) - 0.0.0.0/0
# HTTPS (443) - 0.0.0.0/0
# SSH (22) - Your IP only`,
          explanation: 'This configuration creates a basic web server with proper security settings.'
        },
        example2: {
          title: 'S3 Bucket with Lifecycle Policy',
          description: 'Cost-optimized storage with automated data management',
          code: `{
  "Rules": [
    {
      "ID": "DataLifecycleRule",
      "Status": "Enabled",
      "Transitions": [
        {
          "Days": 30,
          "StorageClass": "STANDARD_IA"
        },
        {
          "Days": 90,
          "StorageClass": "GLACIER"
        },
        {
          "Days": 365,
          "StorageClass": "DEEP_ARCHIVE"
        }
      ]
    }
  ]
}`,
          keyPoints: [
            'Automatically moves data to cheaper storage classes over time',
            'Can reduce storage costs by up to 80% for infrequently accessed data'
          ]
        }
      },
      industryContext: {
        usage: 'Companies like Dropbox use S3 to store exabytes of user data, while Netflix uses EC2 to power its global streaming infrastructure.',
        tools: [
          { name: 'AWS Systems Manager', description: 'Unified interface for managing EC2 instances' },
          { name: 'Amazon CloudWatch', description: 'Monitoring for EC2 and other AWS services' },
          { name: 'AWS Backup', description: 'Centralized backup across AWS services' }
        ],
        careerRelevance: 'Understanding core AWS services is fundamental for cloud roles. These services appear in 90% of AWS job requirements.'
      },
      takeaways: [
        'EC2 provides flexible compute capacity for any workload',
        'S3 offers unlimited, highly durable object storage',
        'RDS simplifies database management and scaling'
      ],
      pitfalls: [
        'Not right-sizing EC2 instances leading to over-provisioning',
        'Leaving S3 buckets publicly accessible unintentionally'
      ],
      nextSteps: [
        'Practice launching different EC2 instance types',
        'Experiment with S3 storage classes and lifecycle policies'
      ]
    }),
    duration: '60 minutes',
    difficulty: 'beginner',
    order: 2,
    learningObjectives: [
      'Launch and configure EC2 instances',
      'Create and manage S3 buckets',
      'Set up RDS databases',
      'Configure VPC networking'
    ],
    prerequisites: ['AWS account access', 'Completion of AWS introduction module'],
    estimatedTime: '60 minutes',
    practice: {
      task: 'Create a complete web application infrastructure using EC2, S3, and RDS. Configure proper security groups and VPC settings.',
      starter: `# Infrastructure Setup Checklist:
# 1. Create a VPC with public and private subnets
# 2. Launch an EC2 instance in the public subnet
# 3. Create an S3 bucket for static assets
# 4. Set up an RDS instance in the private subnet
# 5. Configure security groups for each service`,
      solution: `# Complete Infrastructure Solution:

## 1. VPC Setup
1. Create VPC with CIDR 10.0.0.0/16
2. Create public subnet (10.0.1.0/24)
3. Create private subnet (10.0.2.0/24)
4. Create internet gateway and attach to VPC
5. Create route table for public subnet

## 2. EC2 Web Server
1. Launch t2.micro instance in public subnet
2. Use Amazon Linux 2 AMI
3. Configure security group:
   - HTTP (80) from 0.0.0.0/0
   - SSH (22) from your IP
4. Install web server via user data

## 3. S3 Static Assets
1. Create bucket with unique name
2. Enable static website hosting
3. Upload HTML, CSS, JS files
4. Configure bucket policy for public read

## 4. RDS Database
1. Create MySQL RDS instance in private subnet
2. Configure security group for port 3306
3. Allow access only from EC2 security group
4. Enable automated backups

## 5. Application Integration
1. Configure web application to connect to RDS
2. Store static assets in S3
3. Test end-to-end functionality`,
      hints: [
        'Always use private subnets for databases to enhance security',
        'Test connectivity between services before deploying applications',
        'Use IAM roles instead of hardcoded credentials for EC2 to access other services'
      ]
    },
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'Which EC2 instance type is best for CPU-intensive applications?',
          options: ['t2.micro', 'c5.large', 'm5.large', 'r5.large'],
          correctAnswer: 1
        },
        {
          id: 'q2',
          question: 'What is the maximum size of a single object in S3?',
          options: ['5 GB', '5 TB', '100 GB', '1 TB'],
          correctAnswer: 1
        },
        {
          id: 'q3',
          question: 'Which RDS feature provides automated database backups?',
          options: ['Read Replicas', 'Multi-AZ deployment', 'Automated Backups', 'Parameter Groups'],
          correctAnswer: 2
        }
      ]
    }
  },
  {
    id: 'aws-security-iam',
    title: 'AWS Security and Identity Management (IAM)',
    description: 'Master AWS security fundamentals and IAM best practices',
    content: generateStandardizedContent({
      moduleTitle: 'AWS Security and Identity Management (IAM)',
      courseType: 'aws',
      difficulty: 'intermediate',
      estimatedTime: '75 minutes',
      learningObjectives: [
        'Implement the principle of least privilege using IAM',
        'Create and manage IAM users, groups, and roles',
        'Configure multi-factor authentication for enhanced security',
        'Design secure access patterns for AWS resources'
      ],
      prerequisites: ['Understanding of AWS core services', 'Basic security concepts'],
      keyConceptsData: {
        concept1: {
          title: 'Identity and Access Management (IAM)',
          explanation: 'IAM enables you to manage access to AWS services and resources securely. You can create and manage AWS users and groups, and use permissions to allow and deny their access to AWS resources.',
          importance: 'IAM is the foundation of AWS security. Proper IAM configuration prevents unauthorized access and ensures compliance with security policies.',
          example: 'A development team needs access to EC2 and S3, but not to billing information. IAM policies can grant exactly the required permissions.'
        },
        concept2: {
          title: 'IAM Policies and Permissions',
          explanation: 'IAM policies are JSON documents that define permissions. They specify what actions are allowed or denied on which resources.',
          bestPractices: [
            'Use AWS managed policies when possible for common use cases',
            'Follow the principle of least privilege - grant only necessary permissions',
            'Regularly audit and rotate access keys and permissions'
          ]
        },
        concept3: {
          title: 'IAM Roles and Temporary Credentials',
          explanation: 'IAM roles provide temporary security credentials for applications and services. They eliminate the need to embed long-term credentials in applications.'
        }
      },
      practicalExamples: {
        example1: {
          title: 'Creating a Developer IAM Group',
          description: 'Setting up role-based access for development teams',
          code: `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "ec2:DescribeInstances",
        "ec2:RunInstances",
        "ec2:StopInstances",
        "s3:GetObject",
        "s3:PutObject"
      ],
      "Resource": "*",
      "Condition": {
        "StringEquals": {
          "aws:RequestedRegion": "us-east-1"
        }
      }
    }
  ]
}`,
          explanation: 'This policy allows developers to manage EC2 instances and S3 objects in a specific region only.'
        },
        example2: {
          title: 'EC2 Instance Role for S3 Access',
          description: 'Secure way for applications to access AWS services',
          code: `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:PutObject"
      ],
      "Resource": "arn:aws:s3:::my-app-bucket/*"
    }
  ]
}`,
          keyPoints: [
            'No hardcoded credentials in application code',
            'Temporary credentials that rotate automatically'
          ]
        }
      },
      industryContext: {
        usage: 'Financial institutions like Capital One use IAM to ensure strict access controls and regulatory compliance across thousands of AWS accounts.',
        tools: [
          { name: 'AWS IAM Access Analyzer', description: 'Identifies resources shared with external entities' },
          { name: 'AWS CloudTrail', description: 'Logs all API calls for security auditing' },
          { name: 'AWS Config', description: 'Monitors compliance with security configurations' }
        ],
        careerRelevance: 'IAM expertise is crucial for cloud security roles. Many organizations require IAM knowledge for any AWS-related position.'
      },
      takeaways: [
        'IAM is the cornerstone of AWS security',
        'Always follow the principle of least privilege',
        'Use roles instead of users for applications and services'
      ],
      pitfalls: [
        'Using root account for daily operations',
        'Sharing IAM user credentials between team members'
      ],
      nextSteps: [
        'Practice creating custom IAM policies',
        'Set up MFA for all IAM users'
      ]
    }),
    duration: '75 minutes',
    difficulty: 'intermediate',
    order: 3,
    learningObjectives: [
      'Understand IAM core concepts',
      'Create users, groups, and roles',
      'Write custom IAM policies',
      'Implement security best practices'
    ],
    prerequisites: ['AWS core services knowledge', 'Basic security understanding'],
    estimatedTime: '75 minutes',
    practice: {
      task: 'Design and implement a complete IAM strategy for a multi-team organization with different access requirements.',
      starter: `# IAM Strategy Implementation:
# 1. Create organizational groups (Developers, DevOps, Admins)
# 2. Design appropriate policies for each group
# 3. Set up cross-account roles
# 4. Implement MFA requirements
# 5. Create service roles for applications`,
      solution: `# Complete IAM Implementation:

## 1. Organizational Groups Setup
Groups:
- Developers: EC2, S3, Lambda access in dev environment
- DevOps: Full infrastructure access with deployment permissions  
- Admins: Full account access with billing permissions

## 2. Custom Policies
Developer Policy:
- Allow: EC2 (describe, run, stop), S3 (get, put), Lambda (invoke)
- Deny: Production resource modification
- Condition: Time-based access (business hours)

DevOps Policy:
- Allow: All compute and storage services
- Allow: CloudFormation, CodeDeploy, CodePipeline
- Condition: MFA required for production changes

## 3. Cross-Account Roles
- Create role in production account
- Allow assumption from development account
- Require external ID for additional security

## 4. MFA Implementation
- Enable MFA for all users
- Require MFA for sensitive operations
- Set up backup recovery methods

## 5. Service Roles
- EC2 role for S3 access
- Lambda execution role
- ECS task roles with minimal permissions`,
      hints: [
        'Test policies in a development environment before applying to production',
        'Use AWS Policy Simulator to validate policy effects',
        'Regularly review and audit IAM permissions using Access Advisor'
      ]
    },
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the principle of least privilege?',
          options: [
            'Grant maximum permissions to avoid access issues',
            'Grant only the minimum permissions necessary',
            'Use root account for all operations',
            'Share credentials between users'
          ],
          correctAnswer: 1
        },
        {
          id: 'q2',
          question: 'Which is the most secure way for an EC2 instance to access S3?',
          options: ['Hardcode access keys', 'Store keys in instance metadata', 'Use IAM roles', 'Environment variables'],
          correctAnswer: 2
        },
        {
          id: 'q3',
          question: 'What does MFA stand for in AWS security context?',
          options: ['Multi-Factor Authentication', 'Multiple File Access', 'Managed Firewall Architecture', 'Master Firewall Administrator'],
          correctAnswer: 0
        }
      ]
    }
  }
];
